/**
 * @file hooks/useFetch
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import {useState, useEffect, useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {isFunction} from 'lodash';
import {useOriginalDeepCopy} from '@huse/previous-value';

export interface FetchResult<TParam, TData> {
    loading: boolean;
    reload: (needSetLoading?: boolean) => Promise<any>;
    data?: TData | null;
    error?: Error | null;
    latestPayload?: TParam;
};

const useFetch = <TParam, TData>(
    action: (params?: any) => Promise<TData>,
    params?: TParam,
    $onRequest?: (payload?: any) => any,
    $onResponse?: (result?: any, params?: any) => any,
    $onError?: (ex: any) => void,
    abort?: boolean
): FetchResult<TParam, TData> => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>();
    const [data, setData] = useState<TData | null>();
    const [latestPayload, setLatestPayload] = useState<TParam>();

    const originalParams = useOriginalDeepCopy(params);
    // zhangzhe 2019-12-13 三个回调函数放在refs中 不能用useEffect来监听更新，因为可能出现来不及的情况
    const hooksRef = useRef<any>(null);
    hooksRef.current = {
        $onRequest,
        $onResponse,
        $onError
    };
    const dispatch = useDispatch();

    const runFetch = useCallback(async (needSetLoading: boolean = true) => {
        try {
            if (abort) {
                return;
            }
            if (needSetLoading) {
                setLoading(true);
            }
            setError(null);
            // 发起前的钩子
            const requestPayload = isFunction(hooksRef.current.$onRequest)
                ? (hooksRef.current.$onRequest(originalParams) || originalParams)
                : originalParams;
            setLatestPayload(requestPayload);
            // zhangzhe 2020-03-09 为了兼容没有通过bindActionCreators传来的action
            // FIXME: 这里有更好的实现么？硬编码不太合适
            let result: any = null;
            if (/dispatch\(/.test(action.toString()) && !/actionCreator/.test(action.toString())) {
                result = await dispatch(action(requestPayload));
            }
            else {
                result = await action(requestPayload);
            }

            // 发起后的钩子
            const resultData = isFunction(hooksRef.current.$onResponse)
                ? (hooksRef.current.$onResponse(result, requestPayload) || result)
                : result;
            setData(resultData);
            setLoading(false);
            return result;
        }
        catch (ex) {
            // 错误回调
            if (isFunction(hooksRef.current.$onError)) {
                hooksRef.current.$onError(ex);
            }
            setLoading(false);
            setError(ex);
        }
    }, [action, originalParams, abort, dispatch]);

    // 参数和refreshFlag一改动则重新请求接口
    useEffect(() => {
        runFetch();
    }, [runFetch]);

    return {
        loading,
        data,
        error,
        reload: runFetch,
        latestPayload
    };
};

export default useFetch;
